import { CONTROLLER_OFFSET } from '../physics/physicsConstants'
import { composeYaw } from '../physics/poseMath'
import {
  AVATAR_ID,
  EditablePreviewWorld,
  ObjectRuntimeState,
  Vec3,
} from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { WHISTLE_SFX } from './missionSfx'
import { findStateByName, nextTempId, touchesAvatar } from './sharedHelpers'
import { BehaviorHandler, MissionBehaviors, HudStyle } from './types'

/**
 * RJF11（M11 UFO）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF11.rbxl）のギミックを移植する:
 * - UFO/UFOScript: UFO モデル全体が毎フレーム ②UFO（学習者が動かすパーツ）
 *   の位置へ MoveTo で追従する。②UFO が destroy されたら sting 音 +
 *   「ゲームしゅうりょう！」を表示して UFO を消す。
 * - UFO/Collision/CollisionScript: UFO の当たり判定にキャラクターが触れる
 *   たび（0.3 秒のデバウンス付き）カウント +1 を表示（StarterGui/GUILocalScript）。
 *   終了条件は「②UFO が destroy される」のみで、接触回数では終了しない
 *   （学習者プログラムが「10 回動いたら ②UFO を destroy」して終了させる想定）。
 * - UFO/Collision/SpotLight (Enabled, Face=Bottom, Angle=90, Range=16):
 *   UFO 真下への光の演出。半透明の光柱 + 地面の光だまりで近似する。
 *
 * プレビューでの近似・意図的な差異:
 * - UFO モデルの追従は、Collision を基準に「②UFO との位置差分ぶん全パーツを
 *   平行移動」で再現する（モデル概念がないため）。
 * - カウント・終了表示は HUD テキストで再現する。
 * - 終了音はホイッスル音（実音源）で鳴らす。
 * - 接触判定は Collision 単体ではなく UFO モデル全体（円盤・脚・青い球を含む
 *   全パーツのいずれかの AABB）で行う。実機ではキャラクターがジャンプ時に腕を上げる
 *   などして UFO のどこに触れても Collision（15×5.7×15）に重なるが、プレビューの
 *   アバターは 2×5×2 のカプセルで、UFO 中央の透明な衝突パーツ（BeamPart 等。
 *   下面 Y≈6.8）に頭が止められると Collision 下面（Y≈7.1）へ届かず、青い球の
 *   周辺でしかカウントされなかった。
 */

/** UFO 追従の目標となる学習者パーツ名（Lua: workspace ②UFO）。 */
const TARGET_NAME = '②UFO'

/** UFO の当たり判定パーツ名（モデルの基準にも使う）。 */
const COLLISION_NAME = 'Collision'

/**
 * UFO モデルのパーツ識別半径（studs）。テンプレートの実測では、UFO モデルの
 * 全パーツ（Body・Engine・Lights・脚 Part〜Part3 など）は Collision 中心
 * (60,10,30) から約 8.5 studs 以内にあり、周辺の非 UFO パーツ（ボール 2 個・
 * スタートちてん・Wall）は 30 studs 以上離れているため、12 で確実に分離できる。
 */
const UFO_PART_RADIUS = 12

/**
 * UFO モデルのパーツ識別の最大寸法（studs）。半径内にかかる Baseplate
 * （120×16×120）を除外する。UFO 側の最大パーツは Collision の 15。
 */
const UFO_PART_MAX_SIZE = 20

/** 接触カウントのデバウンス（Lua: wait(0.3)）。 */
const TOUCH_DEBOUNCE_SECONDS = 0.3

/**
 * 接触判定の箱を各方向へ広げる幅（studs）。アバターの KCC は衝突パーツの
 * 手前 CONTROLLER_OFFSET ぶん離して止めるため、頭が BeamPart 等に当たっても
 * AABB 同士は重ならない。物理的に接触した状態を確実に拾える余裕を持たせる。
 */
const TOUCH_MARGIN = CONTROLLER_OFFSET * 5

/** 終了表示の HUD テキスト（実機 StarterGui/ScreenGui1 の文言）。 */
const FINISH_TEXT = 'ゲームしゅうりょう！'

/** 地面（Baseplate 120×16×120, 中心 Y=-8）の上面 Y。光だまりの基準。 */
const GROUND_TOP_Y = 0

/** 光柱の直径（studs）。UFO 下面の BeamPart（8×0.31×8）に合わせる。 */
const LIGHT_BEAM_DIAMETER = 8

/** 地面の光だまりの直径（studs）。SpotLight Angle=90 の広がりを近似。 */
const LIGHT_POOL_DIAMETER = 12

/** 光だまりの厚み（studs）。地面に貼り付く薄い円盤。 */
const LIGHT_POOL_THICKNESS = 0.2

/** 光だまりの中心 Y。地面上面との Z ファイティングを避けて少し浮かせる。 */
const LIGHT_POOL_Y = GROUND_TOP_Y + 0.11

/** 光の色（SpotLight Color は白。Sparkles の黄色に寄せた暖色の白）。 */
const LIGHT_COLOR = '#FFF6C2'

/** 光柱の透明度（うっすら見える光のカーテン）。 */
const LIGHT_BEAM_TRANSPARENCY = 0.75

/** 光だまりの透明度。 */
const LIGHT_POOL_TRANSPARENCY = 0.55

/**
 * Roblox の cylinder は軸=X（長さ=size.x）なので、X 軸を Y 軸へ向ける
 * （Z 軸まわり +90°）クォータニオンで縦向きの円柱にする。
 */
const VERTICAL_CYLINDER_ORIENTATION = {
  x: 0,
  y: 0,
  z: Math.SQRT1_2,
  w: Math.SQRT1_2,
} as const

/**
 * UFO モデルを構成するパーツの ID 一覧を初期配置から解決する。
 * Collision の初期位置の近傍（半径内）にある小さなパーツを UFO 本体とみなす
 * （②UFO 自身は追従の目標なので除外する）。
 */
const collectUfoPartIds = (
  world: EditablePreviewWorld,
  collision: ObjectRuntimeState,
): string[] => {
  const ids: string[] = []
  for (const state of world.getState().values()) {
    if (state.id === AVATAR_ID || state.name === TARGET_NAME) {
      continue
    }
    const dx = state.position.x - collision.position.x
    const dy = state.position.y - collision.position.y
    const dz = state.position.z - collision.position.z
    const within =
      dx * dx + dy * dy + dz * dz <= UFO_PART_RADIUS * UFO_PART_RADIUS
    const small =
      state.size.x <= UFO_PART_MAX_SIZE &&
      state.size.y <= UFO_PART_MAX_SIZE &&
      state.size.z <= UFO_PART_MAX_SIZE
    if (within && small) {
      ids.push(state.id)
    }
  }
  return ids
}

/** Collision 中心からの相対位置で表した、1 パーツぶんの接触判定の箱。 */
interface TouchBox {
  /** 箱の中心（Collision 中心からの相対座標）。 */
  offset: Vec3
  /** 姿勢を考慮した AABB を各方向へ TOUCH_MARGIN 広げた寸法。 */
  size: Vec3
}

/**
 * 姿勢（rotationY ∘ orientation）を考慮したパーツの AABB 半径を返す。
 * 回転した箱の AABB 半径は |R|·half（回転行列の各成分の絶対値と半径の積）。
 */
const orientedHalfExtents = (state: ObjectRuntimeState): Vec3 => {
  const q = composeYaw(state.orientation, state.rotationY)
  const { x, y, z, w } = q
  const hx = state.size.x / 2
  const hy = state.size.y / 2
  const hz = state.size.z / 2
  const r00 = Math.abs(1 - 2 * (y * y + z * z))
  const r01 = Math.abs(2 * (x * y - z * w))
  const r02 = Math.abs(2 * (x * z + y * w))
  const r10 = Math.abs(2 * (x * y + z * w))
  const r11 = Math.abs(1 - 2 * (x * x + z * z))
  const r12 = Math.abs(2 * (y * z - x * w))
  const r20 = Math.abs(2 * (x * z - y * w))
  const r21 = Math.abs(2 * (y * z + x * w))
  const r22 = Math.abs(1 - 2 * (x * x + y * y))
  return {
    x: r00 * hx + r01 * hy + r02 * hz,
    y: r10 * hx + r11 * hy + r12 * hz,
    z: r20 * hx + r21 * hy + r22 * hz,
  }
}

/**
 * UFO モデルの各パーツの AABB を Collision 中心からの相対座標で返す。
 * UFO は Collision を基準に剛体として平行移動するので、毎フレーム
 * 「現在の Collision 位置 + 各箱」のいずれかにアバターが重なれば接触とする。
 * 全体を 1 つの外接箱にまとめると、円盤の外側の空いた角でも反応してしまう
 * ため、パーツ単位で持つ。
 */
const collectUfoTouchBoxes = (
  world: EditablePreviewWorld,
  collision: ObjectRuntimeState,
  partIds: readonly string[],
): TouchBox[] => {
  const boxes: TouchBox[] = []
  for (const id of partIds) {
    const state = world.getState().get(id)
    if (!state) {
      continue
    }
    const half = orientedHalfExtents(state)
    boxes.push({
      offset: {
        x: state.position.x - collision.position.x,
        y: state.position.y - collision.position.y,
        z: state.position.z - collision.position.z,
      },
      size: {
        x: (half.x + TOUCH_MARGIN) * 2,
        y: (half.y + TOUCH_MARGIN) * 2,
        z: (half.z + TOUCH_MARGIN) * 2,
      },
    })
  }
  return boxes
}

/**
 * UFO 真下への光の演出を生成し、[光柱 ID, 光だまり ID] を返す。
 * Lua: Collision/SpotLight (Face=Bottom) の近似。光柱は UFO に追従して動く
 * （UFO パーツに含める）。光だまりは地面に貼り付き、X/Z のみ追従する。
 */
const spawnUfoLight = (
  world: EditablePreviewWorld,
  collision: ObjectRuntimeState,
): [string, string] => {
  const ufoBottomY = collision.position.y - collision.size.y / 2
  const beamLength = Math.max(1, ufoBottomY - GROUND_TOP_Y)
  const beamId = nextTempId(world, 'rjf11_light_beam')
  world.addObject({
    id: beamId,
    name: 'UFOLightBeam',
    position: {
      x: collision.position.x,
      y: GROUND_TOP_Y + beamLength / 2,
      z: collision.position.z,
    },
    // cylinder は軸=X・長さ=size.x、直径=size.y(=size.z)。縦向きに立てる。
    size: { x: beamLength, y: LIGHT_BEAM_DIAMETER, z: LIGHT_BEAM_DIAMETER },
    color: LIGHT_COLOR,
    material: 'Neon',
    shape: 'cylinder',
    orientation: VERTICAL_CYLINDER_ORIENTATION,
    transparency: LIGHT_BEAM_TRANSPARENCY,
    isStatic: true,
    canCollide: false,
    canTouch: false,
  })
  const poolId = nextTempId(world, 'rjf11_light_pool')
  world.addObject({
    id: poolId,
    name: 'UFOLightPool',
    position: {
      x: collision.position.x,
      y: LIGHT_POOL_Y,
      z: collision.position.z,
    },
    size: {
      x: LIGHT_POOL_THICKNESS,
      y: LIGHT_POOL_DIAMETER,
      z: LIGHT_POOL_DIAMETER,
    },
    color: LIGHT_COLOR,
    material: 'Neon',
    shape: 'cylinder',
    orientation: VERTICAL_CYLINDER_ORIENTATION,
    transparency: LIGHT_POOL_TRANSPARENCY,
    isStatic: true,
    canCollide: false,
    canTouch: false,
  })
  return [beamId, poolId]
}

/** タッチカウント GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const COUNT_HUD_STYLE: HudStyle = {
  background: '#AAFFFF',
  color: '#000000',
  left: 0,
}

/** 終了 GUI の実機デザイン（ScreenGui1/TextLabel の実測値）。 */
const FINISH_HUD_STYLE: HudStyle = {
  background: '#FFFF00',
  color: '#005500',
  border: '#00AA00',
  borderWidth: 5,
  wide: true,
  top: 100,
}

export const createRjf11Behaviors = (): MissionBehaviors => {
  let touchCount = 0
  // 接触カウントのデバウンス期限（performance.now 基準。Lua: isTouched + wait(0.3)）。
  let touchCooldownUntilMs = 0
  // 前フレームでアバターが UFO に重なっていたか。実機の Touched は接触の
  // 開始時にしか発火しないため、重なりの立ち上がりエッジでのみカウントする
  // （重なりっぱなしを毎 0.3 秒カウントすると 1 回の接触で 2 回以上増える）。
  let wasTouchingAvatar = false

  /** UFO の追従・終了監視ループ（Lua: UFOScript + GUILocalScript の統合）。 */
  const runUfo: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const collision = findStateByName(world, COLLISION_NAME)
    if (!collision) {
      return
    }
    const collisionId = collision.id
    // 移動の基準でもある Collision は、近傍判定の結果に依らず必ず含める。
    const ufoPartIds = Array.from(
      new Set([collisionId, ...collectUfoPartIds(world, collision)]),
    )
    // 接触判定の箱 = UFO モデル全体（Collision + 円盤・脚・青い球）の各パーツ
    // の AABB。実行時生成の光柱（地面まで届く）は含めない（含めると UFO の
    // 真下に立つだけでカウントされてしまう）。
    const touchBoxes = collectUfoTouchBoxes(world, collision, ufoPartIds)
    // 光の演出。光柱は UFO パーツとして追従させ、光だまりは別途 X/Z 追従。
    const [lightBeamId, lightPoolId] = spawnUfoLight(world, collision)
    ufoPartIds.push(lightBeamId)

    // 見た目転写の対象 = UFO の可視パーツ（初期 transparency < 1。透明な
    // 構造材・Collision・光柱は除外）。実機の ②UFO は完全透明な操作パーツで、
    // 見た目変更プログラムを入れても画面上は何も変わらないが、プレビューでは
    // 学習者の体験を直感的にするため円盤本体へ転写する（意図的な本家差分）。
    const visibleBodyIds = ufoPartIds.filter((id) => {
      if (id === collisionId || id === lightBeamId) {
        return false
      }
      const state = world.getState().get(id)
      return state !== undefined && state.transparency < 1
    })
    // ②UFO の rbxl 初期値（学習者が変更していない状態）。学習者の onPlay は
    // この環境ビヘイビアより先に同期実行され得るため、「behavior 開始時の
    // state」を基準にするとプレイ開始直後の変更を取りこぼす。固定の初期値を
    // 基準に、転写済みの値（mirrored）と異なるフレームでのみ転写する。
    const TARGET_INITIAL_APPEARANCE = {
      color: '#A3A2A5',
      material: 'Plastic',
      transparency: 1,
      visible: true,
    } as const
    const mirrored: {
      color: string
      material: string
      transparency: number
      visible: boolean
    } = { ...TARGET_INITIAL_APPEARANCE }

    /** 学習者による ②UFO の見た目変更を円盤本体のパーツへ転写する。 */
    const mirrorAppearance = (target: ObjectRuntimeState): void => {
      const changedColor = target.color !== mirrored.color
      const changedMaterial = target.material !== mirrored.material
      const changedTransparency = target.transparency !== mirrored.transparency
      const changedVisible = target.visible !== mirrored.visible
      if (
        !changedColor &&
        !changedMaterial &&
        !changedTransparency &&
        !changedVisible
      ) {
        return
      }
      for (const id of visibleBodyIds) {
        if (!world.exists(id)) {
          continue
        }
        if (changedColor) {
          world.setColor(id, target.color)
        }
        if (changedMaterial) {
          world.setMaterial(id, target.material)
        }
        if (changedTransparency) {
          world.setTransparency(id, target.transparency)
        }
        if (changedVisible) {
          world.setVisible(id, target.visible)
        }
      }
      mirrored.color = target.color
      mirrored.material = target.material
      mirrored.transparency = target.transparency
      mirrored.visible = target.visible
    }

    /** 終了演出（Lua: sting 再生 + ScreenGui1 表示 + UFO:Destroy()）。 */
    const finishGame = (): void => {
      playSample(WHISTLE_SFX.url, WHISTLE_SFX.volume, ctx.signal)
      // 終了表示は count とは別スロット。カウントは最終値へ更新して残し、
      // 終了後もさわった回数が見えるようにする（実機は別 GUI で併記）。
      ctx.setHudText?.(String(touchCount), COUNT_HUD_STYLE, 'count')
      ctx.setHudText?.(FINISH_TEXT, FINISH_HUD_STYLE, 'finish')
      for (const id of [...ufoPartIds, lightPoolId]) {
        if (world.exists(id)) {
          world.destroy(id)
        }
      }
    }

    ctx.setHudText?.(String(touchCount), COUNT_HUD_STYLE, 'count')
    try {
      while (!ctx.signal.aborted) {
        const target = findStateByName(world, TARGET_NAME)
        const anchor = world.getState().get(collisionId)
        if (!anchor) {
          return
        }
        if (!target) {
          // Lua: ②UFO が消えたら sting + 終了表示 + UFO を消す。
          finishGame()
          return
        }
        // 学習者の見た目変更（色・素材・透明度・表示/隠す）を円盤へ転写する。
        mirrorAppearance(target)
        // Lua: UFO:MoveTo(②UFO.Position)。Collision を基準に全パーツを平行移動。
        const dx = target.position.x - anchor.position.x
        const dy = target.position.y - anchor.position.y
        const dz = target.position.z - anchor.position.z
        if (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6 || Math.abs(dz) > 1e-6) {
          for (const id of ufoPartIds) {
            if (world.exists(id)) {
              world.moveBy(id, { x: dx, y: dy, z: dz })
            }
          }
          // 光だまりは地面に貼り付いたまま UFO の真下（X/Z のみ）へ追従する。
          if (world.exists(lightPoolId)) {
            world.setPosition(lightPoolId, {
              x: target.position.x,
              y: LIGHT_POOL_Y,
              z: target.position.z,
            })
          }
        }
        // Lua: CollisionScript。接触の開始時（Touched 相当の立ち上がりエッジ、
        // 0.3 秒デバウンス付き）にカウント +1。重なりが続く間は再カウントしない。
        // 判定は UFO モデルの各パーツの AABB（Collision 基準で追従）のいずれか。
        const now = performance.now()
        const isTouchingAvatar = touchBoxes.some((box) =>
          touchesAvatar(
            world,
            {
              x: anchor.position.x + box.offset.x,
              y: anchor.position.y + box.offset.y,
              z: anchor.position.z + box.offset.z,
            },
            box.size,
          ),
        )
        if (
          isTouchingAvatar &&
          !wasTouchingAvatar &&
          now >= touchCooldownUntilMs
        ) {
          touchCooldownUntilMs = now + TOUCH_DEBOUNCE_SECONDS * 1000
          touchCount += 1
          ctx.setHudText?.(String(touchCount), COUNT_HUD_STYLE, 'count')
        }
        wasTouchingAvatar = isTouchingAvatar
        await ctx.wait(FRAME_SECONDS)
      }
    } finally {
      // 停止（abort）後に実行時生成の光が残留しないよう必ず破棄する。
      for (const id of [lightBeamId, lightPoolId]) {
        if (world.exists(id)) {
          world.destroy(id)
        }
      }
    }
  }

  return { onPlay: [runUfo] }
}
